var Emitter = require('../emitter')
var Comm = require('../comm/communication')
var WrongTransform = require('../comm/transform/wrong')
var Util = require('../util')
var Timer = require('../timer')
var YBRequest = require('../comm/request')
var Constants = require('../constants')
var Log = require('../log')

var NpawAppAnalytics = Emitter.extend(
  /** @lends npaw.AppAnalytics.prototype */
  {
    /**
     * This class is the base of npaw appanalytics. Every plugin will have an instance.
     *
     * @param {npaw.Plugin} plugin Parent plugin.
     *
     * @constructs npaw.AppAnalytics
     * @extends npaw.Emitter
     * @memberof npaw
     */
    constructor: function (plugin) {
      /** Parent {@link npaw.Plugin} reference. */
      this.plugin = plugin
      this._beat = new Timer(this._sendBeat.bind(this), 30000)
      this.sessionExpire = Number(this.plugin.storage.getLocal('sessionExpire')) * 1000 || 180000
      this.appAnalyticsStarted = false
      this.appAnalyticsStopped = false
      this._lastNavigation = {}
      this._lastNavigation.page = ''
      this._lastNavigation.route = ''
    },

    /**
     * Is module started
     * @private
     */
    _isModuleStarted: function () {
      return this.appAnalyticsStarted
    },

    /**
     * @alias npaw.AppAnalytics.prototype.begin.
     */
    andBeyond: function () {
      NpawAppAnalytics.prototype.begin.apply(this, arguments)
    },

    /**
     * This method will start appanalytics logic, setting storage as needed.
     * Will call fireSessionStart the first time and fireNav for every subsequent route change.
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     * @param {boolean} [forceNewSession] boolean to force open a new session.
     */
    begin: function (dimensions, forceNewSession) {
      forceNewSession = forceNewSession || false
      if (forceNewSession) {
        this.plugin.storage.removeStorages('appAnalyticsStarted')
        this.plugin.storage.removeStorages('data')
        this.plugin.restartViewTransform()
      }
      if (!this.appAnalyticsStarted) {
        this._comm = new Comm(this.plugin)
        this._comm.addTransform(this.plugin.fastDataTransform)
        this._comm.addTransform(new WrongTransform(this.plugin))
        this._logBeginEvent(dimensions)
        if (this.plugin && this.plugin.storage && typeof this.plugin.storage.getLocal === 'function') {
          this._registeredProperties = this.plugin.storage.getLocal('appAnalyticsRegisteredProperties')
        }
      }
      // send start session with navigation information at beginning
      this.fireSessionStart(dimensions)
    },

    _setLastActive: function () {
      if (!this._firstActive) {
        this._firstActive = this.getFirstActive()
      }
      this.plugin.storage.setStorages('lastactive', new Date().getTime())
    },

    getFirstActive: function () {
      return Number(this.getLastActive()) || 0
    },

    /**
     * Returns the current {@link npaw.Communication} instance.
     *
     * @returns {npaw.Communication} communication instance
     */
    getComm: function () {
      return this._comm
    },

    // Fire
    /**
     * Emits session start request.
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     */
    fireSessionStart: function (dimensions) {
      this._logFireSessionStartEvent(dimensions)
      if (this.appAnalyticsStopped) {
        this.plugin.restartFastDataTransform()
        this._comm = new Comm(this.plugin)
        this._comm.addTransform(this.plugin.fastDataTransform)
        this._comm.addTransform(new WrongTransform(this.plugin))
        this.appAnalyticsStopped = false
      }
      if (!this.appAnalyticsStarted) {
        this.plugin.storage.setLocal('appAnalyticsStarted', 'true')
        this.appAnalyticsStarted = true
        this._processSessionStart(this._getParamsJson(dimensions, null, null, true, true))
        this._setLastActive()
      }
    },

    /**
     * Emits session start request.
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     */
    fireSessionStop: function (params) {
      this._logFireSessionStopEvent(params)
      if (this.appAnalyticsStarted) {
        this.appAnalyticsStopped = true
        this.appAnalyticsStarted = false
        this.plugin.storage.removeStorages('appAnalyticsStarted')
        // this.emit(NpawAppAnalytics.Event.SESSION_STOP, params)
        this._processSessionStop(params)
        this.plugin.storage.removeStorages('data')
        this.plugin.storage.removeStorages('session')
        this.plugin.storage.removeStorages('lastactive')
      }
    },

    /**
     * Stops the session and creates a new one
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     */
    newSession: function (options, params) {
      this._logFireNewSessionListener(options, params)
      if (this.isActive()) {
        if (this.appAnalyticsStarted) {
          this.fireSessionStop()
        } else {
          this.plugin.storage.removeStorages('appAnalyticsStarted')
          this.plugin.storage.removeStorages('data')
          if (this._comm) this._comm.removeTransform(this.plugin.fastDataTransform)
          this.plugin.restartFastDataTransform()
          if (this._comm) this._comm.addTransform(this.plugin.fastDataTransform)
        }
      }
      this.plugin.setOptions(options)
      this.begin(params)
    },

    /**
     * Emits session start request.
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     */
    fireNav: function (dimensions) {
      this._logFireNavListener(dimensions)
      if (this.isActive()) {
        this.appAnalyticsStarted = true
        this._processNavigation(this._getParamsJson(dimensions, null, null, true))
        // this.emit(NpawAppAnalytics.Event.NAV, this._getParamsJson(dimensions, null, null, true))
      }
    },

    _sendExtraBeat: function () {
      if (this.plugin && this.plugin._beat) {
        var now = new Date().getTime()
        var time = this.plugin._beat.chrono.startTime ? (now - this.plugin._beat.chrono.startTime) : 0
        this.plugin._sendBeat(time)
        this.plugin._beat.chrono.startTime = now
      }
      this._setLastActive()
    },

    /**
     * Emits session start request.
     *
     * @param {Object} [params] Object of key:value params to add to the request.
     */
    fireEvent: function (eventName, dimensions, values, topLevelDimensions) {
      this._logFireEventListener(eventName)
      if (this.appAnalyticsStarted) {
        var builtParams = this._getParamsJson(dimensions, values, eventName)
        Util.assign(builtParams.params, topLevelDimensions || {})
        // this.emit(NpawAppAnalytics.Event.EVENT, builtParams)
        this._processCustomEvent(builtParams)
        this._setLastActive()
      }
    },

    /** Register properties sent by the User, to send in all the events
    *
    * @memberof npaw.AppAnalytics.prototype
    */
    register: function (dimensions, values) {
      this._registeredProperties = { dimensions: dimensions, values: values }
      this.plugin.storage.setLocal('appAnalyticsRegisteredProperties', JSON.stringify(this._registeredProperties))
    },

    /** Calls register if registeredProperties is empty
    *
    * @memberof npaw.AppAnalytics.prototype
    */
    registerOnce: function (dimensions, values) {
      if (!this._registeredProperties) {
        this.register(dimensions, values)
      }
    },

    /** Unregister properties registered with register()
    *
    * @memberof npaw.AppAnalytics.prototype
    */
    unregister: function () {
      this._registeredProperties = null
      this.plugin.storage.removeLocal('appAnalyticsRegisteredProperties')
    },

    /**
     * Splits params in dimensions (strings) and values (numbers)
     *
     * @param {Object} [params] Object of key:value params to split before adding to request.
     */
    _getParamsJson: function (dimensions, values, eventName, isNavigaton, isStart) {
      var returnparams = {}
      if (eventName) returnparams.name = eventName
      returnparams.dimensions = dimensions || {}
      returnparams.values = values || {}
      if (this._registeredProperties) {
        for (var key in this._registeredProperties.dimensions) {
          returnparams.dimensions[key] = this._registeredProperties.dimensions[key]
        }
        for (var key2 in this._registeredProperties.values) {
          returnparams.values[key2] = this._registeredProperties.values[key2]
        }
      }
      var paramsObject = { params: returnparams }
      if (isNavigaton) {
        if (paramsObject.params.dimensions.page) {
          paramsObject.params.page = paramsObject.params.dimensions.page
          delete paramsObject.params.dimensions.page
        }
        if (paramsObject.params.dimensions.route) {
          paramsObject.params.route = paramsObject.params.dimensions.route
          delete paramsObject.params.dimensions.route
        }
        if (!isStart) {
          delete paramsObject.params.dimensions
        }
        delete paramsObject.params.values
      }
      return paramsObject
    },

    isActive: function () {
      var started = this.plugin.storage.getLocal('appAnalyticsStarted')
      if (this.appAnalyticsStarted || (started && started !== 'false')) {
        return true
      }
      var now = new Date().getTime()
      if (Number(this.getLastActive()) + this.sessionExpire > now) {
        return true
      }
      return false
    },

    _initAppAnalytics: function () {
      // Is not necessary to create event listeners in the same object
      // this.on(NpawAppAnalytics.Event.NAV, this._navListener.bind(this))
      // this.on(NpawAppAnalytics.Event.SESSION_START, this._sessionStartListener.bind(this))
      // this.on(NpawAppAnalytics.Event.SESSION_STOP, this._sessionStopListener.bind(this))
      // this.on(NpawAppAnalytics.Event.EVENT, this._eventListener.bind(this))
    },

    /**
     *
     * @param willSendEvent
     * @param service
     * @param params
     * @private
     */
    _sendAppAnalytics: function (willSendEvent, service, params) {
      if (this.plugin.isAppAnalyticsEnabled()) {
        params = this.plugin.requestBuilder.buildParams(params, service, [this, this.plugin])

        var data = {
          params: params,
          plugin: this,
          adapter: this.plugin.videos.getAdapter(),
          adsAdapter: this.plugin.videos.getVideo().getAdsAdapter()
        }

        this.emit(willSendEvent, data)

        if (this._comm && params !== null && this.plugin.options.enabled) {
          this.lastServiceSent = service
          var request = new YBRequest(null, service, params)
          if (this.plugin.isMethodPostEnabled()) {
            request.setPostRequest(true)
          }
          this._comm.sendRequest(request)
        }
      }
    },

    // ---------------------------------------- LISTENERS -----------------------------------------

    _processNavigation: function (params) {
      params = this._parseInternalParams(params)
      if (this._checkDifferentNavigation(params)) {
        this._sendAppAnalytics(Constants.WillSendEvent.WILL_SEND_NAV, Constants.Service.NAV, params)

        // start beats
        if (!this._beat.isRunning) this._beat.start()

        Log.notice('[' + this.plugin.fastDataTransform.getSession() + '] ' + Constants.Service.NAV + ' ' + params.route)
        this._lastNavigation.page = params.page
        this._lastNavigation.route = params.route
      } else {
        Log.warn('Same navigation detected and ignored for Page and Route')
      }
    },

    _processSessionStart: function (params) {
      params = this._parseInternalParams(params)
      this._sendAppAnalytics(
        Constants.WillSendEvent.WILL_SEND_SESSION_START,
        Constants.Service.SESSION_START,
        params
      )

      // start beats
      if (!this._beat.isRunning) this._beat.start()

      Log.notice('[' + this.plugin.fastDataTransform.getSession() + '] ' + Constants.Service.SESSION_START + ' ' + params.route)
    },

    _processSessionStop: function (params) {
      params = this._parseInternalParams(params)
      this._sendAppAnalytics(
        Constants.WillSendEvent.WILL_SEND_SESSION_STOP,
        Constants.Service.SESSION_STOP,
        params
      )

      // stop beats
      if (this._beat.isRunning) this._beat.stop()

      Log.notice('[' + this.plugin.fastDataTransform.getSession() + '] ' + Constants.Service.SESSION_STOP + ' ' + params.route)
    },

    _processCustomEvent: function (params) {
      params = this._parseInternalParams(params)
      this._sendAppAnalytics(Constants.WillSendEvent.WILL_SEND_EVENT, Constants.Service.EVENT, params)
      Log.notice('[' + this.plugin.fastDataTransform.getSession() + '] ' + Constants.Service.EVENT + ' ' + params.name)
    },

    /**
     * Process Internal Params object
     * @param params
     * @returns {{}|*|{}}
     * @private
     */
    _parseInternalParams: function (params) {
      if (params) {
        return params.params || params || {}
      } else {
        return {}
      }
    },

    /**
     * Sends beat request
     *
     * @param {number} diffTime Time since the last ping
     *
     * @private
     * @memberof npaw.Plugin.prototype
     */
    _sendBeat: function (diffTime) {
      var params = {
        diffTime: diffTime
      }

      this._sendAppAnalytics(Constants.WillSendEvent.WILL_SEND_BEAT, Constants.Service.BEAT, params)
      if (this) {
        this._setLastActive()
      }
      Log.verbose(Constants.Service.BEAT)
    },

    /**
     * Check if change page/route (to avoid duplicated navigations)
     * @param params
     * @returns {boolean}
     * @private
     */
    _checkDifferentNavigation: function (params) {
      params = params || {}
      var sameRoute = (this._lastNavigation) && (params.route === this._lastNavigation.route)
      var samePage = (this._lastNavigation) && (params.page === this._lastNavigation.page)
      return !sameRoute || !samePage
    },

    // ---------------------------------------- GETTERS -----------------------------------------

    /**
     * Returns a json with the metrics to be reported in beats when changed
     *
     * @memberof npaw.Plugin.prototype
     */
    getSessionMetrics: function () {
      return Util.getMetricsFrom(this.plugin.options['session.metrics'])
    },

    // ----------------------------------------- LOGS -------------------------------------------

    /**
     *
     * @param {*} willSendLog
     * @param {*} service
     * @param {*} params
     */
    _sendPluginLogs: function (willSendLog, service, params) {
      if (this.plugin.isPluginLogsEnabled()) {
        try {
          if (params && params.logType && params.logAction) {
            Log.notice('[' + this.plugin.fastDataTransform.getSession() + '] ' + 'AppAnalyticsLog ' + params.logType + ': Action ' + params.logAction)
          }
        } catch (e) {}
        params = this.plugin.requestBuilder.buildParams(params, service)
        if (this._comm && params !== null && this.plugin.options.enabled) {
          this._comm.sendRequest(new YBRequest(null, service, params))
        }
      }
    },

    _logBeginEvent: function (dimensions) {
      var params = {
        logs: {
          data: dimensions
        },
        logAction: 'beginSession',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_BEGIN, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    },

    _logFireSessionStartEvent: function (dimensions) {
      var params = {
        logs: {
          data: dimensions
        },
        logAction: 'startSession',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_START, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    },

    _logFireSessionStopEvent: function (eventParams) {
      var params = {
        logs: {
          data: eventParams
        },
        logAction: 'stopSession',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_START, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    },

    _logFireEventListener: function (eventName) {
      var params = {
        logs: {
          data: eventName
        },
        logAction: 'eventSession',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_EVENT, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    },

    _logFireNavListener: function (dimensions) {
      var params = {
        logs: {
          data: dimensions
        },
        logAction: 'nav',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_NAV, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    },

    _logFireNewSessionListener: function (options, eventParams) {
      var params = {
        logs: {
          data: options,
          params: eventParams
        },
        logAction: 'newSession',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_NAV, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
    }

  },
  /** @lends npaw.Plugin */
  {
    // Static Members //
    /**
     * List of events that could be fired
     * @enum
     * @event
     */
    Event: {
      NAV: 'nav',
      SESSION_START: 'sessionStart',
      SESSION_STOP: 'sessionStop',
      BEAT: 'beat',
      EVENT: 'event'
    },
    PluginLogs: [
      Constants.Service.APP_ANALYTICS_PLUGIN_LOGS,
      Constants.Service.VIDEO_PLUGIN_LOGS
    ]
  }
)

Util.assign(NpawAppAnalytics.prototype, require('./appanalytics_getters'))

module.exports = NpawAppAnalytics
